import {
  Badge,
  Box,
  Card,
  Center,
  Container,
  Divider,
  Group,
  Stack,
  Text,
  ThemeIcon,
  Title,
  Table,
  Avatar,
  Flex,
  RingProgress,
  NumberFormatter,
} from "@mantine/core";
import {
  IconCalendar,
  IconCheck,
  IconClock,
  IconCloud,
  IconCloudRain,
  IconCloudSnow,
  IconDroplet,
  IconExternalLink,
  IconMapPin,
  IconSun,
  IconWind,
  IconAward,
  IconTrophy,
  IconMedal,
  IconStar,
  IconTarget,
} from "@tabler/icons-react";
import { repositoryPos, repositoryWeb } from "../../_base/_const/_constVar";
import { MessageResponse } from "../../model/MessageResponse";
import { ChartModelPosModel } from "../../model/ChartModelPosModel";
import { useEffect, useRef, useState } from "react";
import { useMediaQuery } from "@mantine/hooks";
import { useNavigate } from "react-router-dom";
import ItemFluctuation from "../ConfigSystem/ItemFluctuation/ItemFluctuation";

export interface RewardPost {
  id: string;
  title: string;
  imageUrl: string;
  url: string;
}

const HomeView = () => {
  const navigate = useNavigate();
  const isSmallScreen = useMediaQuery("(max-width: 768px)");
  const imgSize = isSmallScreen ? 56 : 96;
  const gridCellHeight = isSmallScreen ? "28vh" : "34vh"; // 2 hàng ~ 56-68vh toàn block
  const sectionRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const featuredRef = useRef<HTMLDivElement | null>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const [boxHeight, setBoxHeight] = useState(0);

  useEffect(() => {
    const updateHeight = () => {
      if (boxRef.current) {
        const height = boxRef.current.clientHeight;
        setBoxHeight(height);
      }
    };

    updateHeight();

    // Cập nhật lại khi resize window
    window.addEventListener("resize", updateHeight);

    return () => {
      window.removeEventListener("resize", updateHeight);
    };
  }, []);

  const scrollToSection = (id: number) => {
    const el = sectionRefs.current[id];
    if (el) {
      const y = el.getBoundingClientRect().top + window.scrollY - 80;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };
  const scrollToFeatured = () => {
    if (featuredRef.current) {
      const y =
        featuredRef.current.getBoundingClientRect().top + window.scrollY - 80;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  // Data for featured employees
  const [dataSale, setDataSale] = useState<ChartModelPosModel | null>(null);

  // Thêm state cho bài viết khen thưởng
  const [rewardArticles, setRewardArticles] = useState<any[]>([]);
  const [articlesLoading, setArticlesLoading] = useState(true);
  // Hover state cho ảnh bài viết khen thưởng
  const [hoveredArticleIdx, setHoveredArticleIdx] = useState<number | null>(
    null
  );

  // Countdown data
  const [countdownData, setCountdownData] = useState({
    startDate: new Date("2025-09-22T00:00:00"), // Ngày bắt đầu chiến dịch
    targetDate: new Date("2026-01-01T00:00:00"), // Chính xác 1/1/2026 00:00:00
    currentRevenue: 850000000, // 850 triệu - doanh số hiện tại
    targetRevenue: 1000000000, // 1 tỷ - mục tiêu doanh số
    campaignName: "100 NGÀY CÁN ĐÍCH",
    targetDateDisplay: "1/1/2026", // Hiển thị mốc thời gian
    lastUpdated: new Date(),
  });

  // Tính toán countdown
  const calculateCountdown = () => {
    const now = new Date().getTime();
    const target = countdownData.targetDate.getTime();
    const difference = target - now;

    if (difference > 0) {
      return {
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor(
          (difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
        ),
        minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((difference % (1000 * 60)) / 1000),
      };
    }
    return { days: 0, hours: 0, minutes: 0, seconds: 0 };
  };

  const [countdown, setCountdown] = useState(calculateCountdown());

  // Update countdown every second
  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date().getTime();
      const target = countdownData.targetDate.getTime();
      const difference = target - now;

      if (difference > 0) {
        setCountdown({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor(
            (difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
          ),
          minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((difference % (1000 * 60)) / 1000),
        });
      } else {
        setCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [countdownData.targetDate]);

  // Function để fetch bài viết khen thưởng
  const getRewardArticles = async () => {
    try {
      setArticlesLoading(true);
      const response = await repositoryWeb.get(
        `/api/v1/TblArticlePos/get-list?Skip=0&Take=5`
      );

      if (response && response.success && response.data) {
        // Sắp xếp theo ngày tạo mới nhất và lấy 5 bài đầu
        const sortedArticles = response.data
          .sort(
            (a: any, b: any) =>
              new Date(
                b.createdDate || b.dateCreated || b.createDate
              ).getTime() -
              new Date(a.createdDate || a.dateCreated || a.createDate).getTime()
          )
          .slice(0, 5);

        setRewardArticles(sortedArticles);
      }
    } catch (error) {
      console.error("Error fetching reward articles:", error);
      setRewardArticles([]);
    } finally {
      setArticlesLoading(false);
    }
  };

  useEffect(() => {
    const getData = async () => {
      try {
        // Call cả 2 API cùng lúc
        const [saleResponse] = await Promise.all([
          repositoryPos.get<MessageResponse<ChartModelPosModel>>(
            "/api/v1/CreateSellItem/get-chart-sale"
          ),
          getRewardArticles(), // Gọi function fetch bài viết
        ]);

        if (saleResponse && saleResponse.success) {
          setDataSale(saleResponse.data);
        }
      } catch (e) {
        console.error("Error fetching data:", e);
      }
    };
    getData();
  }, []);

  const removeVietnameseAccents = (str: string) =>
    str
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/đ/g, "d")
      .replace(/Đ/g, "D");

  const getAvatarInitials = (name: string) => {
    if (!name) return "";
    const nameWithoutAccents = removeVietnameseAccents(name);
    const words = nameWithoutAccents
      .trim()
      .split(" ")
      .filter((w) => w.length > 0);
    if (words.length === 1) return words[0].substring(0, 2).toUpperCase();
    const firstChar = words[0].charAt(0);
    const lastChar = words[words.length - 1].charAt(0);
    return (firstChar + lastChar).toUpperCase();
  };

  const getMedalInfo = (index: number) => {
    switch (index) {
      case 0:
        return { icon: <IconTrophy size={18} />, color: "#F59E0B" };
      case 1:
        return { icon: <IconMedal size={18} />, color: "#2563EB" };
      case 2:
        return { icon: <IconStar size={18} />, color: "#9CA3AF" };
      default:
        return { icon: null, color: "#6B7280" };
    }
  };

  // Function format date cho bài viết
  const formatArticleDate = (dateString: string) => {
    if (!dateString) return "Chưa có ngày";
    const date = new Date(dateString);
    return date.toLocaleDateString("vi-VN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };
  const [currentTime, setCurrentTime] = useState(new Date());
  const [weatherData, setWeatherData] = useState<any>(null);
  const [weatherLoading, setWeatherLoading] = useState(true);

  // Update time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Fetch weather data using Open-Meteo API (Free, no API key required)
  useEffect(() => {
    const fetchWeather = async () => {
      try {
        // Hanoi coordinates: 21.0285° N, 105.8542° E
        const HANOI_LAT = 21.0285;
        const HANOI_LON = 105.8542;

        // Open-Meteo API - completely free, no API key needed
        const response = await fetch(
          `https://api.open-meteo.com/v1/forecast?latitude=${HANOI_LAT}&longitude=${HANOI_LON}&current=temperature_2m,relative_humidity_2m,wind_speed_10m,weather_code&hourly=temperature_2m,relative_humidity_2m&timezone=Asia%2FHo_Chi_Minh`,
          {
            method: "GET",
            headers: {
              Accept: "application/json",
            },
            signal: AbortSignal.timeout(5000),
          }
        );

        if (response.ok) {
          const data = await response.json();
          console.log("✅ Open-Meteo API success:", data);

          // Transform Open-Meteo data to match our component structure
          const weatherCode = data.current.weather_code;
          const weatherDescription = getWeatherDescription(weatherCode);

          setWeatherData({
            main: {
              temp: Math.round(data.current.temperature_2m),
              humidity: data.current.relative_humidity_2m,
              wind_speed: data.current.wind_speed_10m,
            },
            weather: [
              {
                main: getWeatherMain(weatherCode),
                description: weatherDescription,
              },
            ],
            name: "Hà Nội",
          });
        } else {
          // Fallback to intelligent mock data
          console.warn("⚠️ Open-Meteo API failed:", response.status);

          const currentHour = new Date().getHours();
          let weatherType = "Clear";
          let temp = 28;
          let humidity = 65;

          if (currentHour >= 6 && currentHour < 12) {
            // Morning
            weatherType = "Clear";
            temp = 26;
            humidity = 70;
          } else if (currentHour >= 12 && currentHour < 18) {
            // Afternoon
            weatherType = "Clear";
            temp = 32;
            humidity = 60;
          } else if (currentHour >= 18 && currentHour < 22) {
            // Evening
            weatherType = "Clouds";
            temp = 28;
            humidity = 75;
          } else {
            // Night
            weatherType = "Clear";
            temp = 24;
            humidity = 80;
          }

          setWeatherData({
            main: {
              temp: temp,
              humidity: humidity,
              wind_speed: Math.random() * 15 + 5, // Random wind speed 5-20 km/h
            },
            weather: [
              {
                main: weatherType,
                description: weatherType === "Clear" ? "trời quang" : "có mây",
              },
            ],
            name: "Hà Nội",
          });
        }
      } catch (error) {
        console.error("❌ Weather API error:", error);
        // Final fallback
        setWeatherData({
          main: { temp: 28, humidity: 75 },
          weather: [{ main: "Clear", description: "trời quang" }],
          name: "Hà Nội",
        });
      } finally {
        setWeatherLoading(false);
      }
    };

    fetchWeather();
    // Update weather every 15 minutes (Open-Meteo has generous rate limits)
    const weatherTimer = setInterval(fetchWeather, 15 * 60 * 1000);
    return () => clearInterval(weatherTimer);
  }, []);

  // Helper function to convert weather code to description
  const getWeatherDescription = (code: number) => {
    const weatherCodes: { [key: number]: string } = {
      0: "trời quang",
      1: "chủ yếu quang",
      2: "mây rải rác",
      3: "nhiều mây",
      45: "sương mù",
      48: "sương mù đóng băng",
      51: "mưa phùn nhẹ",
      53: "mưa phùn vừa",
      55: "mưa phùn dày",
      61: "mưa nhẹ",
      63: "mưa vừa",
      65: "mưa to",
      71: "tuyết rơi nhẹ",
      73: "tuyết rơi vừa",
      75: "tuyết rơi to",
      77: "hạt tuyết",
      80: "mưa rào nhẹ",
      81: "mưa rào vừa",
      82: "mưa rào to",
      85: "tuyết rơi nhẹ",
      86: "tuyết rơi to",
      95: "dông",
      96: "dông có mưa đá",
      99: "dông có mưa đá to",
    };
    return weatherCodes[code] || "trời quang";
  };

  // Helper function to convert weather code to main weather type
  const getWeatherMain = (code: number) => {
    if (code === 0 || code === 1) return "Clear";
    if (code >= 2 && code <= 3) return "Clouds";
    if (code >= 45 && code <= 48) return "Fog";
    if (code >= 51 && code <= 67) return "Rain";
    if (code >= 71 && code <= 86) return "Snow";
    if (code >= 95 && code <= 99) return "Thunderstorm";
    return "Clear";
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString("vi-VN", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString("vi-VN", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const getWeatherIcon = (weatherMain: string) => {
    switch (weatherMain.toLowerCase()) {
      case "clear":
        return <IconSun size={16} />;
      case "clouds":
        return <IconCloud size={16} />;
      case "rain":
        return <IconCloudRain size={16} />;
      case "snow":
        return <IconCloudSnow size={16} />;
      default:
        return <IconCloud size={16} />;
    }
  };

  const getWeatherColor = (weatherMain: string) => {
    switch (weatherMain.toLowerCase()) {
      case "clear":
        return "yellow";
      case "clouds":
        return "gray";
      case "rain":
        return "blue";
      case "snow":
        return "cyan";
      default:
        return "gray";
    }
  };

  // Internal News Data
  const internalNews = [
    {
      id: 1,
      title: "Nhân viên kinh doanh xuất sắc tháng 08/2025",
      date: "Ngày 12 tháng 9 năm 2025",
      subtitle: "Cá nhân là đòn bẩy cho sức mạnh tập thể",
      content:
        "Tháng 08 vừa qua đánh dấu sự biến động lớn tại HACOM, bằng tất cả sự cố gắng và vững vàng chúng ta cam kết vượt lên những thử thách. Và các gương mặt truyền cảm hứng tiếp tục viết lên câu chuyện kinh doanh đầy tự hào, khẳng định vai trò then chốt trong sự đoàn kết nội bộ của chúng ta.",
      image: "/images/internal-news/image15.png",
      facebookLink:
        "https://www.facebook.com/groups/521514798021434/posts/3033043753535180",
      achievements: [
        {
          title:
            "Cá nhân kinh doanh xuất sắc đạt thành tích liên tiếp 3 tháng (6,7,8/2025)",
          images: [
            "/images/internal-news/image18.png",
            "/images/internal-news/image23.png",
          ],
        },
        {
          title:
            "Cá nhân kinh doanh xuất sắc đạt thành tích liên tiếp 2 tháng (7,8/2025)",
          images: [
            "/images/internal-news/image3.png",
            "/images/internal-news/image17.png",
          ],
        },
        {
          title: "Cá nhân xuất sắc tháng 08/2025",
          images: [
            "/images/internal-news/image8.png",
            "/images/internal-news/image11.png",
            "/images/internal-news/image1.png",
          ],
        },
      ],
      editor: "Huỳnh Việt Túc",
    },
    {
      id: 2,
      title: "Những Ngôi Sao Sáng Tháng 7/2025",
      date: "Ngày 08 tháng 08 năm 2025",
      subtitle: "HACOM F5 Năng Lượng",
      content:
        '"Nhân viên xuất sắc chọn lựa quá chuẩn.". "Cảm thấy các nhân viên xuất sắc rất xứng đáng được vinh danh." Hay "Vinh danh nhân viên xuất sắc hàng tháng là một ghi nhận xứng đáng về đóng góp của nhân viên với công ty." là những chia sẻ vô cùng tích cực tại khảo sát quý 02/2025.',
      image: "/images/internal-news/image16.png",
      facebookLink:
        "https://www.facebook.com/groups/521514798021434/posts/3001212356718320",
      achievements: [
        {
          title:
            "Cá nhân kinh doanh xuất sắc đạt thành tích liên tiếp 3 tháng (5,6,7/2025)",
          images: [
            "/images/internal-news/image20.png",
            "/images/internal-news/image28.png",
          ],
        },
        {
          title:
            "Cá nhân kinh doanh xuất sắc đạt thành tích liên tiếp 2 tháng (6,7/2025)",
          images: [
            "/images/internal-news/image34.png",
            "/images/internal-news/image13.png",
            "/images/internal-news/image35.png",
          ],
        },
        {
          title: "Cá nhân xuất sắc tháng 07/2025",
          images: [
            "/images/internal-news/image5.png",
            "/images/internal-news/image14.png",
            "/images/internal-news/image31.png",
            "/images/internal-news/image10.png",
            "/images/internal-news/image6.png",
            "/images/internal-news/image27.png",
            "/images/internal-news/image7.png",
            "/images/internal-news/image33.png",
            "/images/internal-news/image24.png",
          ],
        },
      ],
      editor: "Huỳnh Việt Túc",
    },
    {
      id: 3,
      title: "HACOM cán mốc kinh doanh tháng 6, khép lại quý 2 /2025",
      date: "Ngày 10 tháng 07 năm 2025",
      subtitle: "Khi nào chúng ta thích nghi với toàn bộ những thay đổi?",
      content:
        "Tháng 6/2025 đã khép lại Quý II và chặng đường 6 tháng đầu năm đầy sôi động của HACOM. Đây liệu có phải thời điểm chúng ta thích nghi với toàn bộ những thay đổi? Và tăng tốc cho mùa Back to School bận rộn nhất!",
      image: "/images/internal-news/image4.png",
      facebookLink:
        "https://www.facebook.com/groups/521514798021434/posts/2973378192835070",
      achievements: [
        {
          title: "Chi nhánh đạt thành tích tập thể xuất sắc nhất Quý II/2025",
          images: ["/images/internal-news/image30.png"],
        },
        {
          title:
            "Cá nhân kinh doanh xuất sắc đạt thành tích liên tiếp 03 tháng (4,5,6/2025)",
          images: [
            "/images/internal-news/image36.png",
            "/images/internal-news/image32.png",
          ],
        },
        {
          title:
            "Cá nhân kinh doanh xuất sắc đạt thành tích liên tiếp 02 tháng (5,6/2025)",
          images: [
            "/images/internal-news/image25.png",
            "/images/internal-news/image12.png",
          ],
        },
        {
          title: "Cá nhân xuất sắc tháng 06/2025",
          images: [
            "/images/internal-news/image21.png",
            "/images/internal-news/image2.png",
            "/images/internal-news/image29.png",
            "/images/internal-news/image9.png",
          ],
        },
      ],
      editor: "Huỳnh Việt Túc",
    },
    {
      id: 4,
      title: "Bản tin tuần số 37",
      date: "Ngày 09 tháng 09 năm 2025",
      content:
        "Tin tức nội bộ tuần này với những thông tin quan trọng về nhân sự và hoạt động của công ty.",
      image: "/images/internal-news/image19.png",
      highlights: [
        'Chúc mừng các nhân viên thử việc sau thời gian "thử thách" năng lực đã chính thức "kết duyên" với Nhà HACOM chúng ta!',
        'Khởi động tháng 09 với màn ra mắt không thể ấn tượng hơn là MV ca nhạc "Cảm ơn tình yêu" gửi đến quý Anh Chị Em và Khách hàng thân yêu.',
        "Chỉ còn 2 ngày nữa để hoàn thiện hồ sơ xét học bổng khuyến học HACOM 2025 cho con em của CBNV HACOM.",
      ],
      facebookLink:
        "https://www.facebook.com/61561398747000/videos/2622784644721554",
      editor: "Huỳnh Việt Túc",
    },
  ];

  return (
    <Box
      style={{
        backgroundColor: "#f8f9fa",
        minHeight: "100vh",
        position: "relative",
      }}
    >
      {/* Countdown Widget 100 Ngày Cán Đích - Ở đầu trang */}
      <Box style={{ padding: isSmallScreen ? 12 : 24, paddingBottom: 0 }}>
        <Card
          style={{
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            borderRadius: "20px",
            padding: isSmallScreen ? "16px" : "24px",
            boxShadow:
              "0 10px 30px rgba(102, 126, 234, 0.3), 0 4px 12px rgba(0,0,0,0.15)",
            border: "none",
            color: "white",
            marginBottom: 16,
          }}
        >
          <Stack gap={isSmallScreen ? "sm" : "md"}>
            {/* Header */}
            <Group gap="sm" align="center" justify="center">
              <ThemeIcon
                size={isSmallScreen ? "lg" : "xl"}
                variant="white"
                color="violet"
              >
                <IconTarget size={isSmallScreen ? 24 : 32} />
              </ThemeIcon>
              <Box style={{ textAlign: "center" }}>
                <Text size={isSmallScreen ? "xxl" : "3xl"} fw={900} c="white">
                  {countdownData.campaignName}
                </Text>
                <Text
                  size={isSmallScreen ? "sm" : "lg"}
                  c="rgba(255,255,255,0.9)"
                >
                  Chiến dịch cán đích doanh số 2025 🎯
                </Text>
                <Text
                  size={isSmallScreen ? "md" : "xl"}
                  fw={700}
                  c="#ffd43b"
                  mt="xs"
                >
                  Đích đến: {countdownData.targetDateDisplay}
                </Text>
              </Box>
            </Group>

            {/* Main Content - Responsive Layout */}
            <Box
              style={{
                display: "grid",
                gridTemplateColumns: isSmallScreen ? "1fr" : "1fr 1fr",
                gap: isSmallScreen ? 16 : 24,
                alignItems: "center",
              }}
            >
              {/* Left: Countdown Timer */}
              <Box>
                <Box
                  style={{
                    background: "rgba(255,255,255,0.15)",
                    borderRadius: "16px",
                    padding: isSmallScreen ? "12px" : "16px",
                    backdropFilter: "blur(10px)",
                    textAlign: "center",
                  }}
                >
                  <Text
                    size={isSmallScreen ? "lg" : "xl"}
                    fw={700}
                    c="white"
                    mb="xs"
                  >
                    ⏰ Thời gian còn lại
                  </Text>

                  {/* Countdown Display */}
                  <Flex
                    gap={isSmallScreen ? "sm" : "md"}
                    justify="center"
                    align="center"
                    wrap="wrap"
                  >
                    <Box style={{ textAlign: "center" }}>
                      <Text
                        size={isSmallScreen ? "3xl" : "4xl"}
                        fw={900}
                        c="white"
                        lh={1}
                      >
                        {countdown.days}
                      </Text>
                      <Text
                        size={isSmallScreen ? "sm" : "md"}
                        c="rgba(255,255,255,0.9)"
                      >
                        ngày
                      </Text>
                    </Box>
                    <Text size={isSmallScreen ? "xl" : "2xl"} c="white" mx="xs">
                      :
                    </Text>
                    <Box style={{ textAlign: "center" }}>
                      <Text
                        size={isSmallScreen ? "3xl" : "4xl"}
                        fw={900}
                        c="white"
                        lh={1}
                      >
                        {countdown.hours.toString().padStart(2, "0")}
                      </Text>
                      <Text
                        size={isSmallScreen ? "sm" : "md"}
                        c="rgba(255,255,255,0.9)"
                      >
                        giờ
                      </Text>
                    </Box>
                    <Text size={isSmallScreen ? "xl" : "2xl"} c="white" mx="xs">
                      :
                    </Text>
                    <Box style={{ textAlign: "center" }}>
                      <Text
                        size={isSmallScreen ? "3xl" : "4xl"}
                        fw={900}
                        c="white"
                        lh={1}
                      >
                        {countdown.minutes.toString().padStart(2, "0")}
                      </Text>
                      <Text
                        size={isSmallScreen ? "sm" : "md"}
                        c="rgba(255,255,255,0.9)"
                      >
                        phút
                      </Text>
                    </Box>
                    <Text size={isSmallScreen ? "xl" : "2xl"} c="white" mx="xs">
                      :
                    </Text>
                    <Box style={{ textAlign: "center" }}>
                      <Text
                        size={isSmallScreen ? "3xl" : "4xl"}
                        fw={900}
                        c="white"
                        lh={1}
                      >
                        {countdown.seconds.toString().padStart(2, "0")}
                      </Text>
                      <Text
                        size={isSmallScreen ? "sm" : "md"}
                        c="rgba(255,255,255,0.9)"
                      >
                        giây
                      </Text>
                    </Box>
                  </Flex>
                </Box>
              </Box>

              {/* Right: Progress & Revenue Info */}
              <Box>
                <Box
                  style={{
                    background: "rgba(255,255,255,0.15)",
                    borderRadius: "16px",
                    padding: isSmallScreen ? "12px" : "16px",
                    backdropFilter: "blur(10px)",
                  }}
                >
                  {/* Progress Ring */}
                  <Group justify="center" mb="md">
                    <RingProgress
                      size={isSmallScreen ? 100 : 120}
                      thickness={isSmallScreen ? 10 : 12}
                      sections={[
                        {
                          value:
                            (countdownData.currentRevenue /
                              countdownData.targetRevenue) *
                            100,
                          color: "#ffd43b", // Màu vàng nổi bật
                        },
                      ]}
                      label={
                        <Text
                          ta="center"
                          fw={900}
                          size={isSmallScreen ? "xl" : "2xl"}
                          c="white"
                        >
                          {Math.round(
                            (countdownData.currentRevenue /
                              countdownData.targetRevenue) *
                              100
                          )}
                          %
                        </Text>
                      }
                    />
                  </Group>

                  {/* Revenue Details */}
                  <Stack gap={6}>
                    <Group justify="space-between">
                      <Text
                        size={isSmallScreen ? "sm" : "md"}
                        c="rgba(255,255,255,0.9)"
                      >
                        💰 Hiện tại:
                      </Text>
                      <Text
                        size={isSmallScreen ? "sm" : "md"}
                        fw={700}
                        c="white"
                      >
                        <NumberFormatter
                          value={countdownData.currentRevenue}
                          thousandSeparator=","
                          suffix=" VNĐ"
                        />
                      </Text>
                    </Group>
                    <Group justify="space-between">
                      <Text
                        size={isSmallScreen ? "sm" : "md"}
                        c="rgba(255,255,255,0.9)"
                      >
                        🎯 Mục tiêu:
                      </Text>
                      <Text
                        size={isSmallScreen ? "sm" : "md"}
                        fw={700}
                        c="white"
                      >
                        <NumberFormatter
                          value={countdownData.targetRevenue}
                          thousandSeparator=","
                          suffix=" VNĐ"
                        />
                      </Text>
                    </Group>
                    <Group justify="space-between">
                      <Text
                        size={isSmallScreen ? "sm" : "md"}
                        c="rgba(255,255,255,0.9)"
                      >
                        🚀 Còn lại:
                      </Text>
                      <Text
                        size={isSmallScreen ? "sm" : "md"}
                        fw={700}
                        c="yellow"
                      >
                        <NumberFormatter
                          value={
                            countdownData.targetRevenue -
                            countdownData.currentRevenue
                          }
                          thousandSeparator=","
                          suffix=" VNĐ"
                        />
                      </Text>
                    </Group>
                  </Stack>
                </Box>
              </Box>
            </Box>

            {/* Motivational Message */}
            <Box
              style={{
                background: "rgba(255,255,255,0.1)",
                borderRadius: "12px",
                padding: isSmallScreen ? "8px 12px" : "12px 16px",
                textAlign: "center",
              }}
            >
              <Text size={isSmallScreen ? "sm" : "lg"} c="white" fw={600}>
                💪 "Cùng nhau nỗ lực, cùng nhau thành công!" 🌟
              </Text>
            </Box>
          </Stack>
        </Card>
      </Box>

      {/* Header Section moved to top */}
      <Box style={{ backgroundColor: "#f8f9fa" }}>
        <Container size={"100%"} py={10} px={8}>
          <Center>
            <Stack align="center" gap="sm" style={{ maxWidth: "800px" }}>
              <Badge
                size="xl"
                variant="gradient"
                gradient={{ from: "purple", to: "pink" }}
                style={{
                  fontSize: "14px",
                  fontWeight: 700,
                  letterSpacing: "1px",
                  textTransform: "uppercase",
                  padding: "8px 20px",
                  borderRadius: "25px",
                  boxShadow: "0 4px 15px rgba(128, 90, 213, 0.3)",
                }}
              >
                Tin Tức Nội Bộ
              </Badge>
              <Title
                order={1}
                size="h1"
                fw={900}
                ta="center"
                c="dark"
                style={{
                  fontSize: "clamp(2rem, 5vw, 3.5rem)",
                  lineHeight: 1.1,
                  letterSpacing: "-0.02em",
                  background:
                    "linear-gradient(135deg, #2c3e50 0%, #34495e 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                  textShadow: "0 2px 4px rgba(0,0,0,0.1)",
                }}
              >
                HACOM GROUP
              </Title>
              <Text
                size="md"
                c="dimmed"
                ta="center"
                style={{
                  lineHeight: 1.6,
                  maxWidth: "600px",
                  fontSize: "clamp(0.9rem, 2.5vw, 1.1rem)",
                }}
              >
                Cập nhật những tin tức mới nhất về hoạt động nội bộ và thành
                tích của nhân viên
              </Text>
            </Stack>
          </Center>
        </Container>
      </Box>
      {/* Corner Navigator Session (2x2 grid - 4 góc) */}
      {internalNews.length >= 4 && (
        <Box style={{ padding: isSmallScreen ? 8 : 16 }}>
          <Container size={"100%"} px={0}>
            <Box
              style={{
                border: "1px solid #e9ecef",
                borderRadius: 12,
                overflow: "hidden",
                backgroundColor: "#fff",
                boxShadow: "0 6px 18px rgba(0,0,0,0.06)",
              }}
            >
              <Box
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gridTemplateRows: "1fr 1fr",
                  gap: 6,
                }}
              >
                {/* Top-left */}
                <Box
                  ref={boxRef}
                  style={{
                    overflow: "hidden",
                    borderTopLeftRadius: 12,
                  }}
                >
                  <div
                    style={{
                      paddingLeft: "5px",
                      width: "100%",
                      height: gridCellHeight,
                      objectFit: "cover",
                    }}
                  >
                    <ItemFluctuation
                      isHome={true}
                      heightProps={boxHeight - 75}
                    />
                  </div>
                </Box>

                {/* Top-right */}
                <Box
                  onClick={() => scrollToSection(internalNews[0].id)}
                  style={{
                    cursor: "pointer",
                    overflow: "hidden",
                    borderTopLeftRadius: 12,
                  }}
                >
                  <img
                    src={internalNews[0].image}
                    alt="Corner 1"
                    style={{
                      width: "100%",
                      height: gridCellHeight,
                      objectFit: "cover",
                      display: "block",
                      transition: "transform 200ms ease",
                      transform: "scale(1)",
                      willChange: "transform",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.transform = "scale(1.04)")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.transform = "scale(1)")
                    }
                  />
                </Box>

                {/* Bottom-left */}
                <Box
                  onClick={() => scrollToSection(internalNews[1].id)}
                  style={{
                    cursor: "pointer",
                    overflow: "hidden",
                    borderTopRightRadius: 12,
                  }}
                >
                  <img
                    src={internalNews[1].image}
                    alt="Corner 2"
                    style={{
                      width: "100%",
                      height: gridCellHeight,
                      objectFit: "cover",
                      display: "block",
                      transition: "transform 200ms ease",
                      transform: "scale(1)",
                      willChange: "transform",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.transform = "scale(1.04)")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.transform = "scale(1)")
                    }
                  />
                </Box>
                {/*<Box
                  onClick={() => scrollToSection(internalNews[2].id)}
                  style={{
                    cursor: "pointer",
                    overflow: "hidden",
                    borderBottomLeftRadius: 12,
                  }}
                >
                  <img
                    src={internalNews[2].image}
                    alt="Corner 3"
                    style={{
                      width: "100%",
                      height: gridCellHeight,
                      objectFit: "cover",
                      display: "block",
                      transition: "transform 200ms ease",
                      transform: "scale(1)",
                      willChange: "transform",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.transform = "scale(1.04)")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.transform = "scale(1)")
                    }
                  />
                </Box>*/}

                {/* Bottom-right - symbolic chart tile */}
                <Box
                  onClick={scrollToFeatured}
                  style={{
                    cursor: "pointer",
                    overflow: "hidden",
                    borderBottomRightRadius: 12,
                  }}
                >
                  <Box
                    style={{
                      width: "100%",
                      height: gridCellHeight,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background:
                        "linear-gradient(135deg, #e0f2ff 0%, #f0f9ff 50%, #fff 100%)",
                      position: "relative",
                      transition: "transform 200ms ease",
                      transform: "scale(1)",
                      willChange: "transform",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.transform = "scale(1.04)")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.transform = "scale(1)")
                    }
                  >
                    <svg
                      width="70%"
                      height="70%"
                      viewBox="0 0 200 120"
                      preserveAspectRatio="xMidYMid meet"
                    >
                      <rect
                        x="20"
                        y="60"
                        width="25"
                        height="40"
                        fill="#74c0fc"
                        rx="3"
                      />
                      <rect
                        x="65"
                        y="45"
                        width="25"
                        height="55"
                        fill="#4dabf7"
                        rx="3"
                      />
                      <rect
                        x="110"
                        y="30"
                        width="25"
                        height="70"
                        fill="#228be6"
                        rx="3"
                      />
                      <rect
                        x="155"
                        y="20"
                        width="25"
                        height="80"
                        fill="#1c7ed6"
                        rx="3"
                      />
                      <polyline
                        points="20,55 77,40 122,25 167,15"
                        stroke="#2b8a3e"
                        strokeWidth="3"
                        fill="none"
                      />
                    </svg>
                    <Text
                      fw={700}
                      c="#1c7ed6"
                      style={{ position: "absolute", bottom: 8 }}
                    >
                      Danh sách nhân viên bán hàng tiêu biểu xuất sắc
                    </Text>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Container>
        </Box>
      )}
      {/* Clock & Weather Widget - Fixed Position */}
      <Box
        style={{
          position: "fixed",
          top: "10px",
          right: "10px",
          zIndex: 1000,
          background:
            "linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(248,249,250,0.98) 100%)",
          backdropFilter: "blur(15px)",
          borderRadius: "12px",
          padding: "12px 16px",
          boxShadow: "0 8px 24px rgba(0,0,0,0.15), 0 2px 8px rgba(0,0,0,0.1)",
          border: "1px solid rgba(0,0,0,0.1)",
          minWidth: "280px",
          maxWidth: "320px",
          display: "none",
        }}
        className="sm:block"
      >
        <Stack gap="sm">
          {/* Time */}
          <Group gap="md" align="center">
            <ThemeIcon size="md" variant="light" color="blue">
              <IconClock size={18} />
            </ThemeIcon>
            <Text size="md" fw={700} c="blue">
              {formatTime(currentTime)}
            </Text>
          </Group>

          {/* Date */}
          <Group gap="md" align="center">
            <ThemeIcon size="md" variant="light" color="green">
              <IconCalendar size={18} />
            </ThemeIcon>
            <Text size="sm" fw={600} c="dimmed" style={{ lineHeight: 1.3 }}>
              {formatDate(currentTime)}
            </Text>
          </Group>

          {/* Weather */}
          {!weatherLoading && weatherData && (
            <>
              <Divider size="xs" />
              <Group gap="sm" align="center">
                <ThemeIcon
                  size="sm"
                  variant="light"
                  color={getWeatherColor(weatherData.weather[0].main)}
                >
                  {getWeatherIcon(weatherData.weather[0].main)}
                </ThemeIcon>
                <Stack gap={2} style={{ flex: 1 }}>
                  <Group gap="xs" align="center">
                    <Text size="sm" fw={600} c="dark">
                      {Math.round(weatherData.main.temp)}°C
                    </Text>
                    <Text size="xs" c="dimmed">
                      {weatherData.weather[0].description}
                    </Text>
                  </Group>
                  <Group gap="md">
                    <Group gap={4} align="center">
                      <IconDroplet size={12} color="blue" />
                      <Text size="xs" c="dimmed">
                        {weatherData.main.humidity}%
                      </Text>
                    </Group>
                    <Group gap={4} align="center">
                      <IconWind size={12} color="green" />
                      <Text size="xs" c="dimmed">
                        {Math.round(weatherData.main.wind_speed)} km/h
                      </Text>
                    </Group>
                    <Group gap={4} align="center">
                      <IconMapPin size={12} color="red" />
                      <Text size="xs" c="dimmed">
                        {weatherData.name}
                      </Text>
                    </Group>
                  </Group>
                </Stack>
              </Group>
            </>
          )}
        </Stack>
      </Box>

      {/* Internal News Section */}
      <Box style={{ backgroundColor: "#f8f9fa" }}>
        <Container size={"100%"} py={20} px={8}>
          <Stack gap="md">
            {/* Header moved above */}

            <Stack gap="sm">
              {internalNews.map((news, index) => (
                <Card
                  key={news.id}
                  shadow="sm"
                  radius="md"
                  padding="sm"
                  style={{
                    backgroundColor: "white",
                    border: "1px solid #e9ecef",
                    transition: "all 0.3s ease",
                    margin: "0 4px",
                  }}
                  ref={(el) => (sectionRefs.current[news.id] = el)}
                >
                  <Stack gap="sm">
                    {/* Header */}
                    <Stack gap="xs">
                      <Group
                        justify="space-between"
                        align="flex-start"
                        wrap="wrap"
                      >
                        <Stack gap="xs" style={{ flex: 1, minWidth: "200px" }}>
                          <Title
                            order={3}
                            size="h4"
                            fw={800}
                            c="dark"
                            style={{ lineHeight: 1.2, margin: "0" }}
                          >
                            {news.title}
                          </Title>
                          <Text
                            size="xs"
                            c="dimmed"
                            fw={500}
                            style={{ opacity: 0.8, margin: "0" }}
                          >
                            {news.date}
                          </Text>
                          {news.subtitle && (
                            <Text
                              size="md"
                              c="blue"
                              fw={600}
                              style={{
                                fontStyle: "italic",
                                lineHeight: 1.3,
                                margin: "0",
                              }}
                            >
                              {news.subtitle}
                            </Text>
                          )}
                        </Stack>
                        <Badge
                          size="sm"
                          variant="light"
                          color="blue"
                          onClick={() =>
                            window.open(news.facebookLink, "_blank")
                          }
                          style={{ cursor: "pointer", flexShrink: 0 }}
                        >
                          <Group gap="xs">
                            <IconExternalLink size={14} />
                            <Text
                              size="xs"
                              style={{ display: "none" }}
                              className="sm:block"
                            >
                              Xem trên Facebook
                            </Text>
                            <Text
                              size="xs"
                              style={{ display: "block" }}
                              className="sm:hidden"
                            >
                              Facebook
                            </Text>
                          </Group>
                        </Badge>
                      </Group>
                    </Stack>

                    {/* Main Image */}
                    {news.image && (
                      <Box
                        style={{
                          width: "100%",
                          borderRadius: "4px",
                          overflow: "hidden",
                          border: "1px solid #e9ecef",
                          margin: "4px 0",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        <img
                          src={news.image}
                          alt={news.title}
                          style={{
                            maxWidth: "100%",
                            height: "auto",
                            objectFit: "contain",
                            display: "block",
                          }}
                        />
                      </Box>
                    )}

                    {/* Content */}
                    <Text
                      size="sm"
                      c="dark"
                      style={{ lineHeight: 1.5, margin: "2px 0" }}
                    >
                      {news.content}
                    </Text>

                    {/* Achievements */}
                    {news.achievements && (
                      <Stack gap="xs">
                        {news.achievements.map((achievement, idx) => (
                          <Box key={idx} style={{ margin: "4px 0" }}>
                            <Text
                              fw={600}
                              c="dark"
                              mb="xs"
                              size="sm"
                              style={{
                                fontStyle: "italic",
                                lineHeight: 1.3,
                                margin: "0 0 4px 0",
                              }}
                            >
                              {achievement.title}
                            </Text>
                            <Group gap="md" wrap="wrap">
                              {achievement.images.map((img, imgIdx) => (
                                <Box
                                  key={imgIdx}
                                  style={{
                                    width: "240px",
                                    height: "240px",
                                    borderRadius: "12px",
                                    overflow: "hidden",
                                    border: "3px solid #e9ecef",
                                    cursor: "pointer",
                                    transition: "transform 0.2s ease",
                                    boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
                                  }}
                                  onClick={() => window.open(img, "_blank")}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.transform =
                                      "scale(1.03)";
                                    e.currentTarget.style.boxShadow =
                                      "0 8px 32px rgba(0,0,0,0.2)";
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.transform =
                                      "scale(1)";
                                    e.currentTarget.style.boxShadow =
                                      "0 4px 16px rgba(0,0,0,0.15)";
                                  }}
                                >
                                  <img
                                    src={img}
                                    alt={`Achievement ${imgIdx + 1}`}
                                    style={{
                                      width: "100%",
                                      height: "100%",
                                      objectFit: "contain",
                                      backgroundColor: "#f8f9fa",
                                    }}
                                  />
                                </Box>
                              ))}
                            </Group>
                          </Box>
                        ))}
                      </Stack>
                    )}

                    {/* Highlights for weekly news */}
                    {news.highlights && (
                      <Stack gap="sm">
                        <Text fw={600} c="dark" size="md">
                          Điểm nổi bật:
                        </Text>
                        {news.highlights.map((highlight, idx) => (
                          <Group key={idx} gap="sm" align="flex-start">
                            <ThemeIcon
                              size="sm"
                              variant="light"
                              color="green"
                              style={{ marginTop: 2 }}
                            >
                              <IconCheck size={12} />
                            </ThemeIcon>
                            <Text
                              size="sm"
                              c="dark"
                              style={{ lineHeight: 1.5 }}
                            >
                              {highlight}
                            </Text>
                          </Group>
                        ))}
                      </Stack>
                    )}

                    {/* Footer */}
                    <Group
                      justify="space-between"
                      align="center"
                      pt="sm"
                      style={{
                        borderTop: "1px solid #e9ecef",
                        marginTop: "8px",
                      }}
                      wrap="wrap"
                    >
                      <Text size="xs" c="dimmed" style={{ margin: "0" }}>
                        Biên tập:{" "}
                        <Text span fw={600} c="blue">
                          {news.editor}
                        </Text>
                      </Text>
                      <Text
                        size="xs"
                        c="dimmed"
                        style={{ display: "none", margin: "0" }}
                        className="sm:block"
                      >
                        © Truyền thông nội bộ - VPGD HACOM
                      </Text>
                    </Group>
                  </Stack>
                </Card>
              ))}
            </Stack>
          </Stack>
        </Container>
      </Box>

      {/* Featured Employee Section */}
      <Box ref={featuredRef} style={{ backgroundColor: "#f8f9fa" }}>
        <Container size={"100%"} py={20} px={8}>
          <Card shadow="sm" padding="md" radius="md" withBorder>
            <Stack gap="sm">
              <Group gap="xs">
                <ThemeIcon color="yellow" variant="light">
                  <IconAward size={20} />
                </ThemeIcon>
                <Title order={4} size="h5" fw={600}>
                  Nhân Viên Xuất Sắc
                </Title>
              </Group>

              <Table highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Tên nhân viên</Table.Th>
                    <Table.Th>Doanh thu</Table.Th>
                    <Table.Th>Xếp hạng</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {dataSale?.employeeList?.length ? (
                    dataSale.employeeList
                      .filter(
                        (e) =>
                          (e.employeeName || "").toLowerCase() !==
                          "nguyễn mạnh điệp".toLowerCase()
                      )
                      .map((employee, index) => {
                        const medal = getMedalInfo(index);
                        return (
                          <Table.Tr key={index}>
                            <Table.Td>
                              <Group gap="sm">
                                <Avatar color="blue" radius="xl" size="sm">
                                  {getAvatarInitials(employee.employeeName)}
                                </Avatar>
                                <Text fw={500}>{employee.employeeName}</Text>
                              </Group>
                            </Table.Td>
                            <Table.Td>
                              <Text fw={600} c="green">
                                {employee.totalRevenue?.toLocaleString("vi-VN")}{" "}
                                VND
                              </Text>
                            </Table.Td>
                            <Table.Td>
                              <Group gap="xs" justify="center">
                                {medal.icon}
                                <Badge variant="light">#{index + 1}</Badge>
                              </Group>
                            </Table.Td>
                          </Table.Tr>
                        );
                      })
                  ) : (
                    <Table.Tr>
                      <Table.Td colSpan={3}>
                        <Text ta="center" c="dimmed">
                          Không có dữ liệu
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  )}
                </Table.Tbody>
              </Table>
            </Stack>
          </Card>
        </Container>
      </Box>

      {/* Reward Articles Section */}
      <Box style={{ backgroundColor: "#f8f9fa" }}>
        <Container size={"100%"} py={20} px={8}>
          <Card shadow="sm" padding="md" radius="md" withBorder>
            <Stack gap="md">
              <Group gap="xs">
                <ThemeIcon color="orange" variant="light">
                  <IconTrophy size={20} />
                </ThemeIcon>
                <Title order={4} size="h5" fw={600}>
                  Bài Viết Khen Thưởng Mới Nhất
                </Title>
              </Group>

              {articlesLoading ? (
                <Text ta="center" c="dimmed">
                  Đang tải...
                </Text>
              ) : rewardArticles.length > 0 ? (
                <Box
                  style={{
                    display: "grid",
                    gridTemplateColumns: isSmallScreen
                      ? "1fr"
                      : "repeat(auto-fit, minmax(280px, 1fr))",
                    gap: 16,
                    alignItems: "stretch",
                  }}
                >
                  {rewardArticles.map((article, index) => {
                    const imgSrc =
                      typeof article?.thumnail === "string"
                        ? article.thumnail
                        : undefined;
                    const linkUrl = article?.url || article?.externalUrl;
                    if (!imgSrc) return null;
                    return (
                      <a
                        key={index}
                        href={linkUrl || undefined}
                        // target={linkUrl ? "_blank" : undefined}
                        rel={linkUrl ? "noopener noreferrer" : undefined}
                        title={article?.title || `Bài viết ${index + 1}`}
                        style={{
                          display: "block",
                          width: "100%",
                          textDecoration: "none",
                          color: "inherit",
                          position: "relative",
                          borderRadius: 8,
                          overflow: "hidden",
                          border: "1px solid #e9ecef",
                          boxShadow:
                            hoveredArticleIdx === index
                              ? "0 10px 24px rgba(0,0,0,0.18)"
                              : "0 4px 12px rgba(0,0,0,0.08)",
                          transition:
                            "box-shadow 180ms ease, transform 180ms ease",
                        }}
                        onMouseEnter={() => setHoveredArticleIdx(index)}
                        onMouseLeave={() => setHoveredArticleIdx(null)}
                      >
                        <img
                          src={imgSrc}
                          alt={article?.title || `Bài viết ${index + 1}`}
                          style={{
                            width: "100%",
                            height: 200,
                            objectFit: "cover",
                            display: "block",
                            transform:
                              hoveredArticleIdx === index
                                ? "scale(1.04)"
                                : "scale(1)",
                            transition: "transform 200ms ease",
                          }}
                          loading="lazy"
                        />
                        {/* Overlay khi hover */}
                        <Box
                          style={{
                            position: "absolute",
                            inset: 0,
                            background:
                              "linear-gradient(0deg, rgba(0,0,0,0.5), rgba(0,0,0,0.15))",
                            color: "#fff",
                            display: "flex",
                            alignItems: "flex-end",
                            justifyContent: "space-between",
                            padding: 12,
                            opacity: hoveredArticleIdx === index ? 1 : 0,
                            transition: "opacity 180ms ease",
                          }}
                        >
                          <Text size="sm" fw={600} style={{ marginRight: 8 }}>
                            {article?.title || "Xem bài viết"}
                          </Text>
                          <Badge color="blue" variant="filled" size="sm">
                            Mở liên kết
                          </Badge>
                        </Box>
                        {/* Ẩn dòng URL nhỏ bên dưới ảnh để ảnh hiển thị full */}
                      </a>
                    );
                  })}
                </Box>
              ) : (
                <Text ta="center" c="dimmed">
                  Chưa có bài viết khen thưởng nào
                </Text>
              )}
            </Stack>
          </Card>
        </Container>
      </Box>
    </Box>
  );
};

export default HomeView;
